import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components';
import { resetRemovedThreadStatus } from '../../../../../store/deals/deals.actions';
import { RootState } from '../../../../../store/store';
import { errorI, successI } from './assets';

type Props = {
  onClose: () => void;
  removeDeal: () => void;
  closeAfterGetStatus: () => void;
  open: boolean;
  isOnlyOneDeal?: boolean;
};

export const RemoveThreadModal: React.FC<Props> = ({
  onClose,
  removeDeal,
  open,
  isOnlyOneDeal = false,
  closeAfterGetStatus,
}: Props): JSX.Element => {
  const { removeThreadStatus } = useSelector((state: RootState) => state.deals);

  const dispatch = useDispatch();

  const closeAndExit = () => {
    if (typeof removeThreadStatus === 'string') {
      closeAfterGetStatus();

      setTimeout(() => {
        dispatch(resetRemovedThreadStatus());
      }, 200);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (typeof removeThreadStatus === 'string') {
      timer = setTimeout(() => {
        closeAndExit();
      }, 3000);
    }

    return () => {
      clearTimeout(timer);
    }; // eslint-disable-next-line
  }, [removeThreadStatus]);

  const attrs = {
    Modal: {
      title: '',
      onClose: closeAndExit,
      open,
      empty: typeof removeThreadStatus === 'string',
    },

    body: {
      wrapper: {
        className: 'remove-thread-modal',
      },

      text: {
        className: 'remove-thread-modal-text',
      },

      subMessage: {
        className: 'remove-thread-modal-submessage',
      },

      buttons: {
        wrapper: {
          className: 'remove-thread-modal__buttons',
        },

        yes: {
          className: 'remove-thread-modal__buttons-yes',
          onClick: removeDeal,
        },

        no: {
          className: 'remove-thread-modal__buttons-no',
          onClick: closeAndExit,
        },
      },
    },

    statusBody: {
      wrapper: {
        className: 'remove-thread-modal__status',
      },

      icon: {
        className: 'remove-thread-modal__status-img',
        src: removeThreadStatus === 'success' ? successI : errorI,
      },

      boldMessage: {
        className: 'remove-thread-modal__status-bold-text',
      },

      message: {
        className: 'remove-thread-modal__status-text',
      },
    },
  };

  const actionBody = removeThreadStatus ? null : (
    <div {...attrs.body.wrapper}>
      <div {...attrs.body.text}>
        {`Remove the selected ${
          isOnlyOneDeal ? 'engagement' : 'engagements'
        } from your list?`}
      </div>
      <div {...attrs.body.subMessage}>
        {`(This removes the selected engagements from the Substrata Q 
          dashboard but doesn't delete them from your inbox.)`}
      </div>
      <div {...attrs.body.buttons.wrapper}>
        <button {...attrs.body.buttons.yes}>Remove</button>
        <button {...attrs.body.buttons.no}>Cancel</button>
      </div>
    </div>
  );

  const statusBody =
    typeof removeThreadStatus === 'string' ? (
      <div {...attrs.statusBody.wrapper}>
        <img {...attrs.statusBody.icon} alt="" />
        <div {...attrs.statusBody.boldMessage}>
          {removeThreadStatus === 'success'
            ? `${
                isOnlyOneDeal ? 'Engagement' : 'Engagements'
              } successfully removed`
            : 'Something went wrong'}
        </div>
        <div {...attrs.statusBody.message}>
          {removeThreadStatus === 'success' ? '' : 'Please try again'}
        </div>
      </div>
    ) : null;

  return (
    <Modal {...attrs.Modal}>
      <>
        {actionBody}
        {statusBody}
      </>
    </Modal>
  );
};
